/* ========================================================================== 
   TELEMETRY & USER ANALYTICS DISPATCHER (/database/telemetry.js)

   Analytics telemetry and Popular Hardware activity are deliberately separate:
   - /api/telemetry.php keeps the existing diagnostic/analytics contract.
   - /api/activity.php receives one exact catalog action at a time and is never
     eligible as ML ground truth.
   ========================================================================== */

import {
  state, getCaseStateKey, getCaseModeSelection, getSelectedComponentIdentity
} from './state.js?v=20260819-audit2';
import { getFilteredResult } from './js/runtime-index.js?v=20260819-audit2';
import { debounce, AIO_COMBOBOX_IDS, getComboboxInputValue } from './utils.js?v=20260822-routebridge2';
import { debugRuntimeIssue } from './runtime.js?v=20260819-audit2';
import { catalogIdOf } from './catalog-identity.js?v=20260819-audit2';

const ACTIVITY_ENDPOINT = '/api/activity.php';
const ACTIVITY_SESSION_KEY = 'sff-popular-activity-session-v2';
const VALID_CATALOG_ID = /^[a-f0-9]{24}$/i;
const ACTIVITY_TYPE_MAP = Object.freeze({
  case: 'case',
  gpu: 'gpu',
  cpu: 'air',
  air: 'air',
  aio: 'aio',
  mobo: 'board',
  board: 'board',
  psu: 'psu',
  ram: 'ram'
});
const STATE_KIND_MAP = Object.freeze({
  gpu: 'gpu',
  cpu: 'cpu',
  air: 'cpu',
  aio: 'aio',
  mobo: 'mobo',
  board: 'mobo',
  psu: 'psu',
  ram: 'ram'
});

function verdictToInt(value) {
  return value === true ? 1 : (value === false ? 0 : null);
}

function validCatalogId(value) {
  const id = String(value || '').trim().toLowerCase();
  return VALID_CATALOG_ID.test(id) ? id : '';
}

function activitySessionId() {
  try {
    let existing = sessionStorage.getItem(ACTIVITY_SESSION_KEY) || '';
    if (/^[A-Za-z0-9_-]{16,80}$/.test(existing)) return existing;

    if (globalThis.crypto?.getRandomValues) {
      const bytes = new Uint8Array(18);
      globalThis.crypto.getRandomValues(bytes);
      existing = btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    } else {
      existing = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
    }
    sessionStorage.setItem(ACTIVITY_SESSION_KEY, existing);
    return existing;
  } catch (_) {
    return `ephemeral_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

function activeCaseFromState() {
  const selectedIdx = state.selectedCaseIdx;
  return selectedIdx !== null && state.fetchedCasesGlobal?.[selectedIdx]
    ? state.fetchedCasesGlobal[selectedIdx]
    : null;
}

function activityContext(activeCase) {
  return {
    case_catalog_id: validCatalogId(catalogIdOf(activeCase)),
    case_mode: activeCase ? getCaseModeSelection(activeCase, 'default') : '',
    case_fixed_step: String(state.portableFixedStep || '').trim()
  };
}

function sendActivityPayload(payload) {
  if (!payload?.catalog_id || !payload?.component_type) return false;
  const body = JSON.stringify({
    schema: 2,
    session_id: activitySessionId(),
    source: 'database',
    training_label_eligible: false,
    ...payload
  });

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(ACTIVITY_ENDPOINT, blob)) return true;
    }
    fetch(ACTIVITY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'same-origin'
    }).catch(error => debugRuntimeIssue('Popular Hardware Activity', error));
    return true;
  } catch (error) {
    debugRuntimeIssue('Popular Hardware Activity', error);
    return false;
  }
}

function selectedCatalogActivity(componentType, componentData = {}, activeCase = null) {
  const popularType = ACTIVITY_TYPE_MAP[String(componentType || '').toLowerCase()] || '';
  if (!popularType) return null;

  if (popularType === 'case') {
    const catalogId = validCatalogId(componentData.catalog_id || componentData.catalogId || catalogIdOf(activeCase));
    if (!catalogId) return null;
    return {
      component_type: 'case',
      catalog_id: catalogId,
      component_name: String(componentData.name || activeCase?.name || '').slice(0, 180)
    };
  }

  const stateKind = STATE_KIND_MAP[String(componentType || '').toLowerCase()] || STATE_KIND_MAP[popularType];
  const selected = stateKind ? getSelectedComponentIdentity(stateKind) : null;
  const catalogId = validCatalogId(
    componentData.catalog_id || componentData.catalogId || componentData.id || selected?.catalogId
  );
  if (!catalogId) return null;

  return {
    component_type: popularType,
    catalog_id: catalogId,
    component_name: String(componentData.name || selected?.name || '').slice(0, 180)
  };
}

function dispatchCatalogActivity(action, componentType, componentData = {}, activeCase = null) {
  const target = selectedCatalogActivity(componentType, componentData, activeCase);
  if (!target) return false;
  return sendActivityPayload({
    action: String(action || 'select').toLowerCase(),
    ...activityContext(activeCase),
    ...target
  });
}

/**
 * Records a meaningful whole-build action against every exact catalog component
 * currently in use. Custom/manual hardware is intentionally excluded.
 */
export function logCurrentBuildActivity(action = 'share') {
  const activeCase = activeCaseFromState();
  const cooling = String(document.getElementById('coolingMode')?.value || 'air').toLowerCase();
  const components = [
    ['case', { name: activeCase?.name || '', catalogId: catalogIdOf(activeCase) }],
    ['mobo', getSelectedComponentIdentity('mobo') || {}],
    ['gpu', activeCase?._isAPU ? {} : (getSelectedComponentIdentity('gpu') || {})],
    ['psu', getSelectedComponentIdentity('psu') || {}],
    ['ram', getSelectedComponentIdentity('ram') || {}],
    [cooling === 'liquid' ? 'aio' : 'cpu', getSelectedComponentIdentity(cooling === 'liquid' ? 'aio' : 'cpu') || {}]
  ];

  let sent = 0;
  for (const [type, component] of components) {
    if (dispatchCatalogActivity(action, type, component, activeCase)) sent += 1;
  }
  return sent;
}

function caseTelemetryFields(caseObj) {
  if (!caseObj) return {};
  const dims = Array.isArray(caseObj._dims) ? caseObj._dims : String(caseObj.caseDimensions || '').match(/\d+(?:\.\d+)?/g)?.slice(0, 3).map(Number);
  const known = caseObj._known || {};
  const knownNumber = (flag, value) => flag && Number.isFinite(Number(value)) ? Number(value) : null;
  const positiveOrNull = value => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null;
  return {
    telemetry_schema_version: 3,
    case_key: getCaseStateKey(caseObj),
    case_slug: caseObj.case_slug || caseObj.db_slug || caseObj.slug || caseObj._stableKey || '',
    case_catalog_id: validCatalogId(catalogIdOf(caseObj)) || null,
    case_mode: getCaseModeSelection(caseObj, 'default'),
    max_gpu_thick: knownNumber(known.maxGPUT, caseObj._maxThick),
    max_gpu_height: knownNumber(known.maxGPUH, caseObj._maxGPUH),
    case_length: positiveOrNull(dims?.[0]),
    case_width: positiveOrNull(dims?.[1]),
    case_height: positiveOrNull(dims?.[2]),
    has_spine: caseObj.hasSpine ? 1 : 0
  };
}

function caseLimit(caseObj, knownKey, value) {
  if (!caseObj) return null;
  return caseObj._known?.[knownKey] && Number.isFinite(Number(value)) ? Number(value) : null;
}

/**
 * Existing analytics event + one exact Popular Hardware activity event.
 * The latter is deliberately action-specific so changing a PSU cannot award
 * popularity credit to every other component currently on screen.
 */
export function logComponentSelection(componentType, componentData) {
  const selectedIdx = state.selectedCaseIdx;
  const activeCase = selectedIdx !== null ? state.fetchedCasesGlobal[selectedIdx] : null;

  const gpuName = document.querySelector('#gpuCombobox .combobox-input')?.value.trim() || '';
  const cpuName = document.querySelector('#cpuCoolerCombobox .combobox-input')?.value.trim() || '';
  const aioName = getComboboxInputValue(AIO_COMBOBOX_IDS);
  const moboName = document.querySelector('#moboCombobox .combobox-input')?.value.trim() || '';

  const activeResult = activeCase ? getFilteredResult(selectedIdx) : null;
  const gpuApplicable = activeCase ? !activeCase._isAPU : true;
  const payload = {
    ...caseTelemetryFields(activeCase),
    telemetry_schema_version: 3,
    event_type: 'COMPONENT_SELECTION',
    case_name: activeCase ? activeCase.name : (document.getElementById('caseSearch')?.value || 'Component Selection'),
    gpu_name: gpuApplicable ? gpuName : '',
    cpu_cooler_name: cpuName,
    aio_name: aioName,
    mobo_name: moboName,
    case_selected: !!activeCase,
    label_source: activeResult && typeof activeResult.fits === 'boolean' ? 'deterministic_engine' : 'none',
    training_label_eligible: false,
    case_volume_l: activeCase && Number.isFinite(Number(activeCase._volume ?? activeCase.volume)) ? Number(activeCase._volume ?? activeCase.volume) : null,
    volume_filter_l: parseFloat(document.getElementById('maxVolume')?.value || 10.0),
    volume: activeCase ? Number(activeCase._volume ?? activeCase.volume) : parseFloat(document.getElementById('maxVolume')?.value || 10.0),
    max_gpu_len: activeCase ? caseLimit(activeCase, 'maxGPU', activeCase._maxGPU) : null,
    max_cpu_height: activeCase ? caseLimit(activeCase, 'maxCPU', activeCase._maxCPU) : null,
    max_aio_rad: activeCase ? caseLimit(activeCase, 'maxAIO', activeCase._maxAIO) : null,
    selected_gpu_present: gpuApplicable,
    selected_gpu_len: gpuApplicable ? (componentType === 'gpu' ? componentData.length_mm : parseFloat(document.getElementById('gpuLength')?.value || 304)) : null,
    selected_gpu_thick: gpuApplicable ? (componentType === 'gpu' ? componentData.thickness_mm : parseFloat(document.getElementById('gpuThickness')?.value || 40)) : null,
    selected_gpu_height: gpuApplicable ? (componentType === 'gpu' ? componentData.height_mm : parseFloat(document.getElementById('gpuHeight')?.value || 137)) : null,
    selected_cpu_height: componentType === 'cpu' ? componentData.height_mm : parseFloat(document.getElementById('cpuHeight')?.value || 47),
    psu_type: document.getElementById('psuType')?.value || 'SFX',
    mobo_type: document.getElementById('moboType')?.value || 'ITX',
    layout: activeCase?._layout || state.activeLayout || 'reference',
    cooling_type: document.getElementById('coolingMode')?.value || 'air',
    radiator_size: parseFloat(document.getElementById('radiatorSize')?.value || 0),
    engine_fits_verdict: verdictToInt(activeResult?.fits),
    fits_verdict: verdictToInt(activeResult?.fits)
  };

  fetch('/api/telemetry.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).catch(error => debugRuntimeIssue('Component Telemetry', error));

  dispatchCatalogActivity('select', componentType, componentData || {}, activeCase);
}

/**
 * Debounced slider telemetry remains analytics-only. Physical requirement edits
 * are not product-popularity actions and therefore never hit /api/activity.php.
 */
export const dispatchSliderTelemetryDebounced = debounce(() => {
  const selectedIdx = state.selectedCaseIdx;
  const activeCase = selectedIdx !== null ? state.fetchedCasesGlobal[selectedIdx] : null;

  const gpuName = document.querySelector('#gpuCombobox .combobox-input')?.value.trim() || '';
  const cpuName = document.querySelector('#cpuCoolerCombobox .combobox-input')?.value.trim() || '';
  const aioName = getComboboxInputValue(AIO_COMBOBOX_IDS);

  const activeResult = activeCase ? getFilteredResult(selectedIdx) : null;
  const gpuApplicable = activeCase ? !activeCase._isAPU : true;
  const payload = {
    ...caseTelemetryFields(activeCase),
    telemetry_schema_version: 3,
    event_type: 'SLIDER_ADJUSTMENT',
    case_name: activeCase ? activeCase.name : (document.getElementById('caseSearch')?.value || 'Volume Exploration'),
    case_selected: !!activeCase,
    label_source: activeResult && typeof activeResult.fits === 'boolean' ? 'deterministic_engine' : 'none',
    training_label_eligible: false,
    gpu_name: gpuApplicable ? gpuName : '',
    cpu_cooler_name: cpuName,
    aio_name: aioName,
    case_volume_l: activeCase && Number.isFinite(Number(activeCase._volume ?? activeCase.volume)) ? Number(activeCase._volume ?? activeCase.volume) : null,
    volume_filter_l: parseFloat(document.getElementById('maxVolume')?.value || 10.0),
    volume: parseFloat(document.getElementById('maxVolume')?.value || 10.0),
    max_gpu_len: activeCase ? caseLimit(activeCase, 'maxGPU', activeCase._maxGPU) : null,
    max_cpu_height: activeCase ? caseLimit(activeCase, 'maxCPU', activeCase._maxCPU) : null,
    max_aio_rad: activeCase ? caseLimit(activeCase, 'maxAIO', activeCase._maxAIO) : null,
    selected_gpu_present: gpuApplicable,
    selected_gpu_len: gpuApplicable ? parseFloat(document.getElementById('gpuLength')?.value || 304) : null,
    selected_gpu_thick: gpuApplicable ? parseFloat(document.getElementById('gpuThickness')?.value || 40) : null,
    selected_gpu_height: gpuApplicable ? parseFloat(document.getElementById('gpuHeight')?.value || 137) : null,
    selected_cpu_height: parseFloat(document.getElementById('cpuHeight')?.value || 47),
    psu_type: document.getElementById('psuType')?.value || 'SFX',
    mobo_type: document.getElementById('moboType')?.value || 'ITX',
    layout: activeCase?._layout || state.activeLayout || 'reference',
    cooling_type: document.getElementById('coolingMode')?.value || 'air',
    radiator_size: parseFloat(document.getElementById('radiatorSize')?.value || 0),
    engine_fits_verdict: verdictToInt(activeResult?.fits),
    fits_verdict: verdictToInt(activeResult?.fits)
  };

  fetch('/api/telemetry.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).catch(error => debugRuntimeIssue('Slider Telemetry', error));
}, 800);
